/**
 * Time helpers for the Studio calendar grid.
 *
 * Self-contained: uses date-fns where available, hand-rolls anything
 * we'd otherwise pull a sub-library for. All calls are pure.
 */

import {
  addDays, addMinutes, differenceInMinutes, endOfDay, format,
  isSameDay, parseISO, startOfDay, startOfWeek as dfStartOfWeek,
} from 'date-fns'

/** Calendar view modes. Day & Week are wired in v1; the others are stubs. */
export type CalendarView = 'day' | 'week' | 'month' | 'quarter' | 'year'

/** Calendar grid time range — currently fixed; could be user-configurable later. */
export const DAY_START_HOUR = 5     // 5am — leaves room for early-morning lifts/journal
export const DAY_END_HOUR = 23      // 11pm — caps off late-evening blocks
export const SLOT_MINUTES = 30      // grid resolution; 30 min keeps the row count sane
// PX_PER_HOUR drives every block height. At 80px, a 30-min block is 40px,
// which is enough room for a small time label + a two-line title. Going
// lower clips titles; going higher pushes the calendar past one screen.
export const PX_PER_HOUR = 80
export const PX_PER_MINUTE = PX_PER_HOUR / 60

export const VISIBLE_HOURS = DAY_END_HOUR - DAY_START_HOUR
export const VISIBLE_MINUTES = VISIBLE_HOURS * 60
export const GRID_PX_HEIGHT = VISIBLE_HOURS * PX_PER_HOUR

/** Monday-anchored week start (matches the inspiration screenshot). */
export const startOfWeek = (d: Date) => dfStartOfWeek(d, { weekStartsOn: 1 })

/** The 7 dates of the week containing `d`, Mon → Sun. */
export const weekDates = (d: Date): Date[] => {
  const monday = startOfWeek(d)
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i))
}

/** Format a column header like "MON 18" / "TUE 19". */
export const formatColumnHeader = (d: Date) =>
  format(d, 'EEE d').toUpperCase()

/** Format a row label like "05", "12", "23". 24-hour clock, zero-padded for visual alignment. */
export const formatHourLabel = (h: number) => String(h).padStart(2, '0')

/** Convert a Date to pixel-offset from the top of the grid. */
export const dateToGridY = (d: Date): number => {
  const minutesIntoDay = d.getHours() * 60 + d.getMinutes()
  const minutesFromStart = minutesIntoDay - DAY_START_HOUR * 60
  return Math.max(0, minutesFromStart * PX_PER_MINUTE)
}

/** Compute (top, height) for a block within a single day's column. */
export interface GridPosition {
  topPx: number
  heightPx: number
  /** Whether the block's start was before our visible window (overflow indicator). */
  overflowTop: boolean
  /** Whether the block extends past our visible window. */
  overflowBottom: boolean
}

export function computeBlockGridPosition(
  starts: Date,
  ends: Date,
): GridPosition {
  const dayStart = new Date(starts.getFullYear(), starts.getMonth(), starts.getDate(), DAY_START_HOUR, 0)
  const dayEnd = new Date(starts.getFullYear(), starts.getMonth(), starts.getDate(), DAY_END_HOUR, 0)
  const clampedStart = starts < dayStart ? dayStart : starts
  const clampedEnd = ends > dayEnd ? dayEnd : ends
  const topPx = differenceInMinutes(clampedStart, dayStart) * PX_PER_MINUTE
  // 22px floor matches the min height where a single-line "time · title"
  // layout in BlockTile still reads cleanly.
  const heightPx = Math.max(22, differenceInMinutes(clampedEnd, clampedStart) * PX_PER_MINUTE)
  return {
    topPx,
    heightPx,
    overflowTop: starts < dayStart,
    overflowBottom: ends > dayEnd,
  }
}

/** Parse a server ISO timestamp into a Date. Returns null for falsy input. */
export function parseServerDate(s: string | null | undefined): Date | null {
  if (!s) return null
  try {
    return parseISO(s)
  } catch {
    return null
  }
}

/** Block-on-day predicate. Returns true if the block (which may be a multi-day) intersects `day`. */
export function blockIntersectsDay(
  startsAt: Date,
  endsAt: Date | null,
  day: Date,
): boolean {
  if (!endsAt) return isSameDay(startsAt, day)
  return startsAt < endOfDay(day) && endsAt > startOfDay(day)
}

/** 24-hour time label, e.g. "09:30" / "13:00". Always HH:MM zero-padded. */
export const formatTimeLabel = (d: Date): string => {
  const hh = d.getHours().toString().padStart(2, '0')
  const mm = d.getMinutes().toString().padStart(2, '0')
  return `${hh}:${mm}`
}

/** Return [starts, ends] for the given listBlocks window. */
export function rangeForView(anchor: Date, view: CalendarView): { starts: Date; ends: Date } {
  const start = view === 'day' ? startOfDay(anchor) : startOfDay(startOfWeek(anchor))
  const ends = view === 'day' ? endOfDay(anchor) : addDays(start, 7)
  return { starts: start, ends }
}

/** Format yyyy-MM-dd for ISO API params. */
export const isoDay = (d: Date) => format(d, "yyyy-MM-dd'T'00:00:00.000xxx")

/** Convenience: derive ends-at from starts-at + duration, defaulting to 30 min. */
export const deriveEnds = (starts: Date, durationMinutes: number | null | undefined) =>
  addMinutes(starts, durationMinutes && durationMinutes > 0 ? durationMinutes : 30)

/** Drag-snap grain. 15-min slots are the standard for calendar tools. */
export const SNAP_MINUTES = 15
export const snapToSlot = (minutes: number) =>
  Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES
